import { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { fmt, fmtPct, fmtDollars } from '../format';
import HelpLink from '../components/HelpLink';

export default function WipReportPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPMs, setExpandedPMs] = useState(new Set());
  const [activeTab, setActiveTab] = useState('wip');
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.getWipActive()
      .then(d => {
        setData(d);
        setExpandedPMs(new Set());
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.project_name || '').toLowerCase().includes(q) ||
      (r.project_number || '').toLowerCase().includes(q) ||
      (r.project_manager || '').toLowerCase().includes(q) ||
      (r.project_state || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(row => {
      const pm = row.project_manager || 'Unassigned';
      if (!map.has(pm)) map.set(pm, []);
      map.get(pm).push(row);
    });
    return map;
  }, [filtered]);

  const togglePM = (pm) => {
    setExpandedPMs(prev => {
      const next = new Set(prev);
      next.has(pm) ? next.delete(pm) : next.add(pm);
      return next;
    });
  };

  const expandAll = () => setExpandedPMs(new Set(grouped.keys()));
  const collapseAll = () => setExpandedPMs(new Set());

  const totals = useMemo(() => sumRows(filtered), [filtered]);
  const meta = data[0] || {};

  if (loading) return <div className="page-loading">Loading...</div>;

  const fyLabel = meta.fiscal_year || '';
  const reportDate = meta.data_refreshed_at
    ? new Date(meta.data_refreshed_at).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'Unknown';
  const priorSnapshotDate = meta.prior_snapshot_date
    ? new Date(meta.prior_snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const TABS = [
    { id: 'wip', label: 'WIP Schedule', helpId: 'wip-schedule-tab' },
    { id: 'risk', label: 'Risk & Exposure', helpId: 'risk-exposure-tab' },
    { id: 'je', label: 'Journal Entries', helpId: 'journal-entries-tab' },
  ];

  return (
    <div className="page wip-page">
      <div className="page-header">
        <div>
          <h1>Work in Progress</h1>
          <p className="page-subtitle">Current Year Contracts Completed &amp; In Progress &mdash; {data.length} projects</p>
        </div>
        <div className="page-actions">
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="table-search"
          />
          <button className="btn btn-ghost" onClick={expandAll}>Expand All</button>
          <button className="btn btn-ghost" onClick={collapseAll}>Collapse All</button>
          <button className="btn btn-secondary" onClick={() => window.print()}>Print Report</button>
        </div>
      </div>

      <div className="report-header-bar">
        <div className="report-title">CM Corp &mdash; Work in Progress Schedule</div>
        <div className="report-meta">
          <span>FY {fyLabel}</span>
          <span className="meta-sep">|</span>
          <span>Data as of {reportDate}</span>
        </div>
      </div>

      <div className="tab-bar">
        {TABS.map(t => (
          <button key={t.id} className={`tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.label} <HelpLink id={t.helpId} />
          </button>
        ))}
      </div>

      {activeTab === 'wip' && <WipTable grouped={grouped} expandedPMs={expandedPMs} togglePM={togglePM} totals={totals} />}
      {activeTab === 'risk' && <RiskTable grouped={grouped} expandedPMs={expandedPMs} togglePM={togglePM} totals={totals} />}
      {activeTab === 'je' && <JournalEntryTable grouped={grouped} expandedPMs={expandedPMs} togglePM={togglePM} totals={totals} priorDate={priorSnapshotDate} />}

      <div className="wip-position-summary">
        <div className="position-card underbilled">
          <div className="position-label">Costs and Estimated Earnings in Excess of Billings</div>
          <div className="position-value">{fmtDollars(totals.underbilled)}</div>
        </div>
        <div className="position-card overbilled">
          <div className="position-label">Billings in Excess of Costs and Estimated Earnings</div>
          <div className="position-value">{fmtDollars(totals.overbilled)}</div>
        </div>
        <div className="position-card net">
          <div className="position-label">Net WIP Position</div>
          <div className="position-value">{fmtDollars(totals.underbilled - totals.overbilled)}</div>
        </div>
      </div>
    </div>
  );
}

function WipTable({ grouped, expandedPMs, togglePM, totals }) {
  return (
    <div className="table-container wip-table-container">
      <table className="data-table wip-table">
        <thead>
          <tr className="wip-header-group">
            <th colSpan="2" className="header-group-empty"></th>
            <th colSpan="4" className="header-group">Contract Values</th>
            <th colSpan="4" className="header-group">Current Year</th>
            <th colSpan="4" className="header-group">Prior Years</th>
            <th colSpan="6" className="header-group">From Inception to Date</th>
            <th colSpan="2" className="header-group">At Period End</th>
          </tr>
          <tr>
            <th className="col-id">No.</th>
            <th className="col-project">Project</th>
            <th className="col-money">Est. Revenue</th>
            <th className="col-money">Proj. Costs</th>
            <th className="col-money">Est. GP</th>
            <th className="col-pct">GP %</th>
            <th className="col-money">Revenue</th>
            <th className="col-money">Costs</th>
            <th className="col-money">GP</th>
            <th className="col-pct">GP %</th>
            <th className="col-money">Revenue</th>
            <th className="col-money">Costs</th>
            <th className="col-money">GP</th>
            <th className="col-pct">GP %</th>
            <th className="col-money">Earned Rev.</th>
            <th className="col-money">Costs TD</th>
            <th className="col-money">Billings</th>
            <th className="col-money">CTC</th>
            <th className="col-pct">% Comp.</th>
            <th className="col-money">GP</th>
            <th className="col-money">Under-Billed</th>
            <th className="col-money">Over-Billed</th>
          </tr>
        </thead>
        <tbody>
          {renderGroupedRows(grouped, expandedPMs, togglePM, (pm, pmT) => [
            td('money sub', fmt(pmT.revenue)), td('money sub', fmt(pmT.projCosts)),
            td(`money sub ${pmT.gp < 0 ? 'negative' : ''}`, fmt(pmT.gp)), td('pct sub', fmtPct(pmT.revenue > 0 ? pmT.gp / pmT.revenue : 0)),
            td('money sub', fmt(pmT.cyRev)), td('money sub', fmt(pmT.cyCosts)),
            td(`money sub ${pmT.cyGp < 0 ? 'negative' : ''}`, fmt(pmT.cyGp)), td('pct sub', fmtPct(pmT.cyRev > 0 ? pmT.cyGp / pmT.cyRev : 0)),
            td('money sub', fmt(pmT.pyRev)), td('money sub', fmt(pmT.pyCosts)),
            td(`money sub ${pmT.pyGp < 0 ? 'negative' : ''}`, fmt(pmT.pyGp)), td('pct sub', fmtPct(pmT.pyRev > 0 ? pmT.pyGp / pmT.pyRev : 0)),
            td('money sub', fmt(pmT.earnedRev)), td('money sub', fmt(pmT.costsToDate)),
            td('money sub', fmt(pmT.billings)), td('money sub', fmt(pmT.ctc)),
            td('pct sub', ''), td(`money sub ${pmT.inceptionGp < 0 ? 'negative' : ''}`, fmt(pmT.inceptionGp)),
            td('money sub', fmt(pmT.underbilled)), td('money sub', fmt(pmT.overbilled)),
          ], r => [
            td('money', fmt(r.estimated_contract_revenue)), td('money', fmt(r.projected_total_costs)),
            td(`money ${r.estimated_gross_profit < 0 ? 'negative' : ''}`, fmt(r.estimated_gross_profit)), td('pct', fmtPct(r.budget_gp_pct)),
            td('money', fmt(r.cy_earned_revenue)), td('money', fmt(r.cy_contract_costs)),
            td(`money ${r.cy_gross_profit < 0 ? 'negative' : ''}`, fmt(r.cy_gross_profit)), td('pct', fmtPct(r.cy_gp_pct)),
            td('money', fmt(r.py_earned_revenue)), td('money', fmt(r.py_contract_costs)),
            td(`money ${r.py_gross_profit < 0 ? 'negative' : ''}`, fmt(r.py_gross_profit)), td('pct', fmtPct(r.py_gp_pct)),
            td('money', fmt(r.earned_contract_revenue)), td('money', fmt(r.contract_costs_to_date)),
            td('money', fmt(r.contract_billings)), td('money', fmt(r.estimated_costs_to_complete)),
            td('pct', fmtPct(r.pct_complete)), td(`money ${r.inception_gross_profit < 0 ? 'negative' : ''}`, fmt(r.inception_gross_profit)),
            td('money', fmt(r.costs_in_excess_of_billings)), td('money', fmt(r.billings_in_excess_of_costs)),
          ])}
        </tbody>
        <tfoot>{grandTotalRow(totals, [
          td('money', fmt(totals.revenue)), td('money', fmt(totals.projCosts)),
          td('money', fmt(totals.gp)), td('pct', fmtPct(totals.revenue > 0 ? totals.gp / totals.revenue : 0)),
          td('money', fmt(totals.cyRev)), td('money', fmt(totals.cyCosts)),
          td('money', fmt(totals.cyGp)), td('pct', fmtPct(totals.cyRev > 0 ? totals.cyGp / totals.cyRev : 0)),
          td('money', fmt(totals.pyRev)), td('money', fmt(totals.pyCosts)),
          td('money', fmt(totals.pyGp)), td('pct', fmtPct(totals.pyRev > 0 ? totals.pyGp / totals.pyRev : 0)),
          td('money', fmt(totals.earnedRev)), td('money', fmt(totals.costsToDate)),
          td('money', fmt(totals.billings)), td('money', fmt(totals.ctc)),
          td('pct', ''), td('money', fmt(totals.inceptionGp)),
          td('money', fmt(totals.underbilled)), td('money', fmt(totals.overbilled)),
        ])}</tfoot>
      </table>
    </div>
  );
}

function RiskTable({ grouped, expandedPMs, togglePM, totals }) {
  return (
    <div className="table-container wip-table-container">
      <table className="data-table wip-table">
        <thead>
          <tr className="wip-header-group">
            <th colSpan="2" className="header-group-empty"></th>
            <th colSpan="3" className="header-group">Fade Analysis</th>
            <th colSpan="1" className="header-group">Backlog</th>
            <th colSpan="1" className="header-group">Retainage</th>
            <th colSpan="4" className="header-group">Change Order Exposure</th>
          </tr>
          <tr>
            <th className="col-id">No.</th>
            <th className="col-project">Project</th>
            <th className="col-pct">Orig. GP%</th>
            <th className="col-pct">Curr. GP%</th>
            <th className="col-pct">Fade</th>
            <th className="col-money">Backlog</th>
            <th className="col-money">Retainage Held</th>
            <th className="col-money">Approved COs</th>
            <th className="col-money">Pending COs</th>
            <th className="col-money">Draft COs</th>
            <th className="col-money">Total Exposure</th>
          </tr>
        </thead>
        <tbody>
          {renderGroupedRows(grouped, expandedPMs, togglePM, (pm, pmT) => [
            td('pct sub', ''), td('pct sub', ''), td('pct sub', ''),
            td('money sub', fmt(pmT.backlog)), td('money sub', fmt(pmT.retainage)),
            td('money sub', fmt(pmT.coApproved)), td('money sub', fmt(pmT.coPending)),
            td('money sub', fmt(pmT.coDraft)), td('money sub', fmt(pmT.coExposure)),
          ], r => [
            td('pct', fmtPct(r.original_gp_pct)), td('pct', fmtPct(r.budget_gp_pct)),
            td(`pct ${r.margin_fade < 0 ? 'negative' : ''}`, fmtPct(r.margin_fade)),
            td('money', fmt(r.backlog)), td('money', fmt(r.retainage_held)),
            td('money', fmt(r.co_approved_value)), td('money', fmt(r.co_pending_value)),
            td('money', fmt(r.co_draft_value)), td('money', fmt(r.co_exposure)),
          ])}
        </tbody>
        <tfoot>{grandTotalRow(totals, [
          td('pct', ''), td('pct', ''), td('pct', ''),
          td('money', fmt(totals.backlog)), td('money', fmt(totals.retainage)),
          td('money', fmt(totals.coApproved)), td('money', fmt(totals.coPending)),
          td('money', fmt(totals.coDraft)), td('money', fmt(totals.coExposure)),
        ])}</tfoot>
      </table>
    </div>
  );
}

function JournalEntryTable({ grouped, expandedPMs, togglePM, totals, priorDate }) {
  return (
    <div className="table-container wip-table-container">
      <table className="data-table wip-table">
        <thead>
          <tr className="wip-header-group">
            <th colSpan="2" className="header-group-empty"></th>
            <th colSpan="2" className="header-group">Current Position</th>
            <th colSpan="2" className="header-group">Prior{priorDate ? ` (${priorDate})` : ''}</th>
            <th colSpan="3" className="header-group">Journal Entry</th>
          </tr>
          <tr>
            <th className="col-id">No.</th>
            <th className="col-project">Project</th>
            <th className="col-money">Under-Billed</th>
            <th className="col-money">Over-Billed</th>
            <th className="col-money">Under-Billed</th>
            <th className="col-money">Over-Billed</th>
            <th className="col-money">11200</th>
            <th className="col-money">21000</th>
            <th className="col-money">42700</th>
          </tr>
        </thead>
        <tbody>
          {renderGroupedRows(grouped, expandedPMs, togglePM, (pm, pmT) => [
            td('money sub', fmt(pmT.underbilled)), td('money sub', fmt(pmT.overbilled)),
            td('money sub', fmt(pmT.priorUnderbilled)), td('money sub', fmt(pmT.priorOverbilled)),
            td('money sub', fmt(pmT.je11200)), td('money sub', fmt(pmT.je21000)), td('money sub', fmt(pmT.je42700)),
          ], r => [
            td('money', fmt(r.costs_in_excess_of_billings)), td('money', fmt(r.billings_in_excess_of_costs)),
            td('money', fmt(r.prior_underbilled)), td('money', fmt(r.prior_overbilled)),
            td(`money ${r.je_11200 < 0 ? 'negative' : ''}`, fmt(r.je_11200)),
            td(`money ${r.je_21000 < 0 ? 'negative' : ''}`, fmt(r.je_21000)),
            td(`money ${r.je_42700 < 0 ? 'negative' : ''}`, fmt(r.je_42700)),
          ])}
        </tbody>
        <tfoot>{grandTotalRow(totals, [
          td('money', fmt(totals.underbilled)), td('money', fmt(totals.overbilled)),
          td('money', fmt(totals.priorUnderbilled)), td('money', fmt(totals.priorOverbilled)),
          td('money', fmt(totals.je11200)), td('money', fmt(totals.je21000)), td('money', fmt(totals.je42700)),
        ])}</tfoot>
      </table>
    </div>
  );
}

function td(cls, content) {
  const isNeg = cls.includes('negative');
  const isSub = cls.includes('sub');
  const base = cls.replace(' sub', '').replace(' negative', '').trim();
  return <td className={`col-${base}${isSub ? ' subtotal' : ''}${isNeg ? ' negative' : ''}`}>{content}</td>;
}

// Helper: render PM group rows + project detail rows
function renderGroupedRows(grouped, expandedPMs, togglePM, pmCellsFn, projectCellsFn) {
  return [...grouped.entries()].map(([pm, projects]) => {
    const expanded = expandedPMs.has(pm);
    const pmT = sumRows(projects);
    return [
      <tr key={`pm-${pm}`} className="pm-row" onClick={() => togglePM(pm)}>
        <td className="col-id"><span className={`expand-icon ${expanded ? 'expanded' : ''}`}>&#9656;</span></td>
        <td className="col-project pm-name">{pm} <span className="pm-count">({projects.length})</span></td>
        {pmCellsFn(pm, pmT)}
      </tr>,
      ...(expanded ? projects.map(r => (
        <tr key={r.project_id} className="project-row">
          <td className="col-id">{r.project_number}</td>
          <td className="col-project" title={r.project_name}>
            {r.project_name}
            <a href={`https://app.procore.com/9528/${r.project_id}/project/home`}
               target="_blank" rel="noopener noreferrer" className="procore-link"
               onClick={e => e.stopPropagation()} title="Open in Procore">&#8599;</a>
          </td>
          {projectCellsFn(r)}
        </tr>
      )) : [])
    ];
  });
}

// Helper: grand total footer row
function grandTotalRow(totals, cells) {
  return (
    <tr className="grand-total-row">
      <td></td>
      <td className="col-project">Grand Total</td>
      {cells}
    </tr>
  );
}

function sumRows(rows) {
  return rows.reduce((acc, r) => ({
    revenue:         acc.revenue + (r.estimated_contract_revenue || 0),
    projCosts:       acc.projCosts + (r.projected_total_costs || 0),
    gp:              acc.gp + (r.estimated_gross_profit || 0),
    cyRev:           acc.cyRev + (r.cy_earned_revenue || 0),
    cyCosts:         acc.cyCosts + (r.cy_contract_costs || 0),
    cyGp:            acc.cyGp + (r.cy_gross_profit || 0),
    pyRev:           acc.pyRev + (r.py_earned_revenue || 0),
    pyCosts:         acc.pyCosts + (r.py_contract_costs || 0),
    pyGp:            acc.pyGp + (r.py_gross_profit || 0),
    earnedRev:       acc.earnedRev + (r.earned_contract_revenue || 0),
    costsToDate:     acc.costsToDate + (r.contract_costs_to_date || 0),
    billings:        acc.billings + (r.contract_billings || 0),
    ctc:             acc.ctc + (r.estimated_costs_to_complete || 0),
    inceptionGp:     acc.inceptionGp + (r.inception_gross_profit || 0),
    underbilled:     acc.underbilled + (r.costs_in_excess_of_billings || 0),
    overbilled:      acc.overbilled + (r.billings_in_excess_of_costs || 0),
    backlog:         acc.backlog + (r.backlog || 0),
    retainage:       acc.retainage + (r.retainage_held || 0),
    coApproved:      acc.coApproved + (r.co_approved_value || 0),
    coPending:       acc.coPending + (r.co_pending_value || 0),
    coDraft:         acc.coDraft + (r.co_draft_value || 0),
    coExposure:      acc.coExposure + (r.co_exposure || 0),
    priorUnderbilled: acc.priorUnderbilled + (r.prior_underbilled || 0),
    priorOverbilled:  acc.priorOverbilled + (r.prior_overbilled || 0),
    je11200:         acc.je11200 + (r.je_11200 || 0),
    je21000:         acc.je21000 + (r.je_21000 || 0),
    je42700:         acc.je42700 + (r.je_42700 || 0),
  }), {
    revenue: 0, projCosts: 0, gp: 0,
    cyRev: 0, cyCosts: 0, cyGp: 0,
    pyRev: 0, pyCosts: 0, pyGp: 0,
    earnedRev: 0, costsToDate: 0, billings: 0, ctc: 0, inceptionGp: 0,
    underbilled: 0, overbilled: 0,
    backlog: 0, retainage: 0,
    coApproved: 0, coPending: 0, coDraft: 0, coExposure: 0,
    priorUnderbilled: 0, priorOverbilled: 0,
    je11200: 0, je21000: 0, je42700: 0,
  });
}
